// Comfy-Paw redesign product page behaviour for sections/cpr-product.liquid.
// Gallery dots/thumbs, variant matching, quantity, AJAX add to cart (opens the Horizon cart drawer,
// falls back to a normal form post), and the sticky mobile add-to-cart bar.
import { CartAddEvent } from '@theme/events';

class CprProduct {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('form[data-product-form], form.cpr-form');
    this.variants = JSON.parse(root.querySelector('script[data-variants]')?.textContent || '[]');
    this.idInput = root.querySelector('[data-variant-id]');
    this.addButton = root.querySelector('[data-add-button]');
    this.status = root.querySelector('[data-status]');
    this.track = root.querySelector('[data-gallery-track]');
    this.abort = new AbortController();
    this.initGallery();
    this.initOptions();
    this.initQuantity();
    this.initSubmit();
    this.initSticky();
  }

  // ---------- Gallery ----------
  initGallery() {
    if (!this.track) return;
    const slides = [...this.track.querySelectorAll('.cpr-gallery__slide')];
    const goButtons = [...this.root.querySelectorAll('[data-gallery-go]')];
    const current = this.root.querySelector('[data-gallery-current]');
    const { signal } = this.abort;

    const setActive = (index) => {
      for (const btn of goButtons) {
        btn.setAttribute('aria-current', String(Number(btn.dataset.galleryGo) === index));
      }
      if (current) current.textContent = String(index + 1);
    };

    for (const btn of goButtons) {
      btn.addEventListener('click', () => this.goTo(Number(btn.dataset.galleryGo)), { signal });
    }

    if ('IntersectionObserver' in window && slides.length > 1) {
      this.galleryObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) setActive(Number(entry.target.dataset.index));
          }
        },
        { root: this.track, threshold: [0.6] }
      );
      for (const slide of slides) this.galleryObserver.observe(slide);
    }
  }

  /** @param {number} index */
  goTo(index) {
    const slide = this.track?.querySelector(`.cpr-gallery__slide[data-index="${index}"]`);
    if (!slide || !this.track) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
  }

  /** @param {number|string} mediaId */
  goToMedia(mediaId) {
    if (!mediaId || !this.track) return;
    const slide = this.track.querySelector(`.cpr-gallery__slide[data-media-id="${mediaId}"]`);
    if (slide) this.goTo(Number(slide.getAttribute('data-index')));
  }

  // ---------- Options ----------
  initOptions() {
    this.fieldsets = [...this.root.querySelectorAll('fieldset[data-option-position]')];
    if (!this.fieldsets.length) return;
    this.root.addEventListener('change', (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.classList.contains('cpr-opt__input')) this.onOptionChange();
    }, { signal: this.abort.signal });
    this.markAvailability();
  }

  selectedOptions() {
    return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value ?? null);
  }

  /** @param {(string|null)[]} options */
  findVariant(options) {
    return this.variants.find((v) => v.options.every((value, i) => value === options[i]));
  }

  onOptionChange() {
    const options = this.selectedOptions();
    this.fieldsets.forEach((fs, i) => {
      const label = fs.querySelector('[data-option-selected]');
      if (label) label.textContent = options[i] ?? '';
    });
    const variant = this.findVariant(options);
    this.markAvailability();
    this.applyVariant(variant);
  }

  // Strike through values that have no available variant given the other current selections.
  markAvailability() {
    const options = this.selectedOptions();
    this.fieldsets.forEach((fs, i) => {
      for (const input of fs.querySelectorAll('input')) {
        const test = [...options];
        test[i] = input.value;
        const match = this.findVariant(test);
        input.dataset.unavailable = String(!match || !match.available);
      }
    });
  }

  /** @param {any} variant */
  applyVariant(variant) {
    const priceNow = this.root.querySelector('[data-price-now]');
    const priceWas = this.root.querySelector('[data-price-was]');
    const wasValue = this.root.querySelector('[data-price-was-value]');
    const save = this.root.querySelector('[data-price-save]');
    const stickyPrice = this.root.querySelector('[data-sticky-price]');

    if (!variant) {
      this.setButtons(false, this.addButton?.dataset.labelUnavailable || 'Unavailable');
      return;
    }
    if (this.idInput) this.idInput.value = String(variant.id);
    if (priceNow) priceNow.textContent = variant.price;
    if (stickyPrice) stickyPrice.textContent = variant.price;
    if (priceWas && wasValue && save) {
      const onSale = Boolean(variant.compare);
      priceWas.hidden = !onSale;
      save.hidden = !onSale;
      wasValue.textContent = variant.compare || '';
      save.textContent = onSale ? `Save ${variant.save}` : '';
    }
    this.setButtons(variant.available, variant.available ? this.addButton?.dataset.labelAdd : this.addButton?.dataset.labelSold);
    this.goToMedia(variant.media);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url.toString());
  }

  /** @param {boolean} enabled @param {string|undefined} label */
  setButtons(enabled, label) {
    for (const btn of [this.addButton, this.root.querySelector('[data-sticky-add]')]) {
      if (!btn) continue;
      btn.disabled = !enabled;
      if (label) btn.textContent = label;
    }
  }

  // ---------- Quantity ----------
  initQuantity() {
    const input = this.root.querySelector('[data-qty]');
    if (!(input instanceof HTMLInputElement)) return;
    for (const btn of this.root.querySelectorAll('[data-qty-step]')) {
      btn.addEventListener('click', () => {
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.getAttribute('data-qty-step')));
        input.value = String(next);
      }, { signal: this.abort.signal });
    }
  }

  // ---------- Add to cart ----------
  initSubmit() {
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal: this.abort.signal });
  }

  /** @param {SubmitEvent} event */
  async onSubmit(event) {
    event.preventDefault();
    const button = this.addButton;
    if (!button || button.disabled) return;
    const label = button.textContent;
    const quantity = Math.max(1, parseInt(this.root.querySelector('[data-qty]')?.value || '1', 10) || 1);
    const variantId = Number(this.idInput?.value);
    button.disabled = true;
    button.textContent = button.dataset.labelAdding || 'Adding…';
    if (this.status) this.status.textContent = '';

    let added = false;
    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((el) => el instanceof HTMLElement ? el.dataset.sectionId : '')
        .filter(Boolean);
      const response = await fetch(`${this.root.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        // Shopify returns a readable message (for example a quantity limit); show it instead of leaving the page.
        if (this.status) this.status.textContent = data.description || data.message || 'Sorry, that could not be added. Please try again.';
        return;
      }
      added = true;
      const cart = await (await fetch(`${this.root.dataset.cartUrl}.js`)).json();
      document.dispatchEvent(
        new CartAddEvent({}, `cpr-product-${this.root.dataset.sectionId}`, {
          source: 'cpr-product',
          itemCount: cart.item_count,
          variantId: String(variantId),
          sections: data.sections,
        })
      );
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else {
        window.location.href = this.root.dataset.cartUrl || '/cart';
      }
    } catch (error) {
      if (added) {
        window.location.href = this.root.dataset.cartUrl || '/cart';
      } else {
        this.form?.submit();
      }
    } finally {
      button.disabled = false;
      button.textContent = label;
    }
  }

  // ---------- Sticky bar (mobile) ----------
  initSticky() {
    const sticky = this.root.querySelector('[data-sticky]');
    const stickyAdd = this.root.querySelector('[data-sticky-add]');
    if (!sticky || !this.addButton || !('IntersectionObserver' in window)) return;

    stickyAdd?.addEventListener('click', () => {
      if (this.form && typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.addButton);
      else this.addButton?.click();
    }, { signal: this.abort.signal });

    this.stickyObserver = new IntersectionObserver(([entry]) => {
      // Show only once the main button has scrolled up out of view, not before the shopper reaches it.
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', String(!show));
      if (stickyAdd instanceof HTMLElement) stickyAdd.tabIndex = show ? 0 : -1;
    });
    this.stickyObserver.observe(this.addButton);
  }

  destroy() {
    this.abort.abort();
    this.galleryObserver?.disconnect();
    this.stickyObserver?.disconnect();
  }
}

const instances = new WeakMap();
function init() {
  for (const root of document.querySelectorAll('[data-cpr-product]')) {
    if (!(root instanceof HTMLElement) || instances.has(root)) continue;
    instances.set(root, new CprProduct(root));
  }
}
init();
document.addEventListener('shopify:section:load', init);
document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target instanceof HTMLElement ? event.target.querySelector('[data-cpr-product]') : null;
  if (root && instances.has(root)) {
    instances.get(root).destroy();
    instances.delete(root);
  }
});
